<template>
  <v-card
    light
    min-height="480"
  >
    <h-top-bar
      :editable="editable"
      @close="$emit('close')"
    />
    <v-card-text class="px-0">
      <h-order-header />
      <v-divider />
      <h-alert v-if="$page.props.brand === 'SBL' && maxAmountExceeded" type="error">
        <span v-html="__('maxAmountExceededNotice').formatUnicorn(maxAmount)" />
      </h-alert>
      <h-dish-orders v-if="editable && useDishOrder" />
      <!-- ORDER CONTENTS -->
      <h-contents-list
        :contents="order.orderContents"
        :disabled="disabled"
        :use-dish-order="useDishOrder"
        @open="openContent"
      />
      <!-- DISCOUNT -->
      <v-fade-transition>
        <div v-if="order.discount">
          <v-divider />
          <h-contents-subheader
            :disabled="disabled"
            v-text="__('discount')"
          />
          <v-list class="pt-0">
            <v-list-item
              :disabled="disabled"
              class="pl-6 pr-3"
              @click.stop="$store.dispatch('orders/dialogs/openDiscounts')"
            >
              <v-row no-gutters>
                <v-col cols="8">
                  {{ order.discount.name }}
                </v-col>
                <v-col cols="4" class="pl-4 text-right">
                  -{{ order.orderDiscountAmount | currency }}
                </v-col>
              </v-row>
            </v-list-item>
          </v-list>
        </div>
      </v-fade-transition>
      <!-- ORDER ID -->
      <span
        v-if="! editable"
        class="white text-caption primary--text pl-2"
        style="position: absolute; left: 0; bottom: 0; width: 100%; height: 24px;"
      >
        #{{ order.id }}
      </span>
      <!-- FABS -->
      <h-order-fabs
        v-if="! editable"
        :visible="visible"
        @pay="pay()"
        @edit="$emit('edit')"
        @close="$emit('close')"
        @finish="$emit('finish')"
        @sendnote="sendKitchenNote"
      />
    </v-card-text>
    <v-scale-transition origin="bottom center">
      <h-summary v-show="editable" />
    </v-scale-transition>
    <h-bottom-bar
      v-if="editable"
      @close="$emit('close')"
      @pay="pay()"
      @sendnote="sendKitchenNote"
    />

    <!-- DIALOGS -->
    <h-fs-dialog :value="showCatalog" top>
      <h-catalog />
    </h-fs-dialog>
    <h-fs-dialog :value="showContent" bottom>
      <h-content :content="content" />
    </h-fs-dialog>
    <h-fs-dialog :value="showDiscounts" bottom>
      <h-discounts />
    </h-fs-dialog>
    <h-fs-dialog :value="showPayment" bottom>
      <h-payment @paid="$emit('close', true)" />
    </h-fs-dialog>
  </v-card>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ComparesVersions from '@/Mixins/ComparesVersions'
  import Currenciable from '@/Mixins/Currenciable'
  import I18n from '@/Mixins/I18n'
  import ManagesTurns from '@/Mixins/ManagesTurns'
  import NeedsPermission from '@/Mixins/NeedsPermission'
  import HFsDialog from '@/Components/FullScreenDialog'
  import HAlert from '@/Components/Messages/Alert'
  import HTopBar from './TopBar'
  import HOrderHeader from './Header'
  import HDishOrders from './DishOrders'
  import HContentsList from './Contents/List'
  import HContentsSubheader from './Contents/Subheader'
  import HOrderFabs from './Fabs'
  import HSummary from './Summary'
  import HBottomBar from './BottomBar'
  import HCatalog from './Dialogs/Catalog/Card'
  import HContent from './Dialogs/Content/Card'
  import HDiscounts from './Dialogs/Discount/Card'
  import HPayment from './Dialogs/Payment/Card'

  export default {
    mixins: [
      ComparesVersions,
      Currenciable,
      I18n,
      ManagesTurns,
      NeedsPermission,
    ],

    components: {
      HFsDialog,
      HAlert,
      HTopBar,
      HOrderHeader,
      HDishOrders,
      HContentsSubheader,
      HContentsList,
      HOrderFabs,
      HSummary,
      HBottomBar,
      HCatalog,
      HContent,
      HDiscounts,
      HPayment,
    },

    props: {
      visible: {
        required: true,
        type: Boolean,
      },
      editable: {
        required: true,
        type: Boolean,
      },
    },

    data: () => ({
      content: {},
    }),

    computed: {
      ...mapState('orders', {
        order: 'current',
      }),
      ...mapState('orders', {
        orderLocked: 'locked',
      }),
      ...mapState('orders/dialogs', {
        dialogsLocked: 'locked',
      }),
      maxAmount () {
        return this.$store.getters['fiscalCountry/get']('orderMaxAmount')
      },
      maxAmountExceeded () {
        return this.maxAmount
          ? this.order.total.greaterThan(this.maxAmount)
          : false
      },
      disabled () {
        return ! this.editable || this.orderLocked || this.dialogsLocked
      },
      useDishOrder () {
        return !! this.$store.getters['orders/config']('shouldOrderDishes')
      },
      ...mapGetters('orders/dialogs', {
        showCatalog: 'catalog',
        showContent: 'content',
        showDiscounts: 'discounts',
        showPayment: 'payment',
      }),
    },

    methods: {
      openContent (content) {
        this.content = content
        this.$log(`Edit OrderContent [${content._index}]: ${content.id || 'NULL'}`, 'Order', this.order.id)
        this.$store.dispatch('orders/dialogs/openContent')
      },
      pay () {
        if (this.order.isEmpty) {
          return
        }
        if (this.$page.props.brand === 'SBL' && this.maxAmountExceeded) {
          this.$store.dispatch('alert/showWarning', this.__('maxAmountExceededWarning').formatUnicorn(this.maxAmount))
          return
        }
        this.$store.dispatch('cashControl/getTurn')
          .then(() => this.openPayment())
          .catch(e => this.manageTurn(e))
      },
      openPayment () {
        if (! this.editable) {
          this.$emit('edit', 'payment')
          return
        }
        this.$store.dispatch('orders/payment/reset')
        this.$store.dispatch('orders/dialogs/openPayment')
      },
      async sendKitchenNote ({id, name}) {
        if (this.orderLocked) {
          return
        }
        this.$log(`Sended kitchen note ${id} "${name}"`, 'Order', this.order.id)
        if (this.canSendHandyActions) {
          this.$store.commit('orders/pendingAction', {
            type: 'printKitchenNote',
            data: { id }
          })
        } else {
          this.$store.commit('orders/sendKitchenNote', id)
        }
        if (! this.editable) {
          await this.$store.dispatch('orders/stash')
        }
        this.$emit('close')
      },
    },
  }
</script>
